import React, { useCallback } from "react";
import { ArchivedHrDataRetentionPolicyDto } from "../../../../common/service/server/controller/CollectionGroupsController";
import { usePrimaryOrganizationId } from "../../../hooks";
import { NetworkRequestState } from "../../../../common/models";
import { Api } from "../../../../common/service/Api";
import { useDataRequest } from "../../../../common/hooks/api";
import { StandardPageWrapper } from "../../../components/layout/StandardPageWrapper";
import { ApiRouteHelpers } from "../../../../common/root/apiRoutes";
import { Download } from "@mui/icons-material";
import { Routes } from "../../../root/routes";
import { Breadcrumbs } from "@truevault/aquilo";

export const useArchivedHrDataRetentionPolicies = (
  organizationId: string,
): [ArchivedHrDataRetentionPolicyDto[], NetworkRequestState, () => Promise<void>] => {
  const apiRequest = useCallback(
    () => Api.consumerGroups.getArchivedHrDataRetentionPolicies(organizationId),
    [organizationId],
  );

  const {
    result: archivedHrDataRetentionPolicies,
    request,
    refresh,
  } = useDataRequest({
    queryKey: ["archivedHrDataRetentionPolicies", organizationId],
    api: apiRequest,
    notReady: !organizationId,
    dependencies: [organizationId],
  });

  return [archivedHrDataRetentionPolicies, request, refresh];
};

export function HRDataRetentionPolicyDocuments() {
  const orgId = usePrimaryOrganizationId();

  const [archivedHrDataRetentionPolicies, request] = useArchivedHrDataRetentionPolicies(orgId);

  const sortedArchivedHrPolicies = (archivedHrDataRetentionPolicies || []).sort((a, b) => {
    return parseRawDate(b.datePublished).getTime() - parseRawDate(a.datePublished).getTime();
  });

  return (
    <StandardPageWrapper requests={[request]}>
      <div className="tw-boundary">
        <Breadcrumbs
          title="HR Data Retention Policies"
          source="Privacy Docs"
          sourceRoute={Routes.documents.root}
        />
      </div>
      {sortedArchivedHrPolicies?.length == 0 && (
        <div className="empty-page">
          <img src="/assets/images/stay_compliant/empty-page.svg" alt="add" />
          <p>No Privacy Policies</p>
        </div>
      )}
      {sortedArchivedHrPolicies?.length > 0 && (
        <div className="mt-md">
          <div className="completed-dpa-table-row">
            <div className="text-weight-bold">Name</div>
            <div className="text-weight-bold text-center">Date Completed</div>
            <div className="text-weight-bold text-center">Download</div>
          </div>
          <hr />
          {sortedArchivedHrPolicies.map((policy) => (
            <div className="completed-dpa-table-row" key={policy.pdfFilename}>
              <div className="font-weight-medium">{policy.name}</div>
              <div className="text-center">{formattedRawDate(policy.datePublished)}</div>
              <div className="text-center">
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={
                    ApiRouteHelpers.organization.collectionGroups.downloadArchivedHrDataRetentionPolicyPdf(
                      orgId,
                    ) + `?pdfFilename=${policy.pdfFilename}`
                  }
                >
                  <Download />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </StandardPageWrapper>
  );
}

function parseRawDate(datePublished: string): Date {
  const year = datePublished.substring(0, 4);
  const month = datePublished.substring(4, 6);
  const day = datePublished.substring(6, 8);
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
}

function formattedRawDate(datePublished: string): string {
  return parseRawDate(datePublished).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
